import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dataMaintenanceApi } from "../../services/phase1Api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ShieldCheck, AlertTriangle, Database, Loader2, Lock, RefreshCw,
  Trash2, RotateCcw, Building2, CheckCircle2, XCircle, Search, Upload, Archive,
} from "lucide-react";

const Stat = ({ label, value, tone = "default" }) => (
  <div className={`rounded-xl border p-4 ${tone === "danger" ? "border-red-200 bg-red-50" : tone === "warn" ? "border-amber-200 bg-amber-50" : "border-border bg-muted/30"}`}>
    <div className="text-2xl font-bold text-foreground">{value}</div>
    <div className="text-[11px] uppercase tracking-wider text-foreground/40 mt-1">{label}</div>
  </div>
);

const ProgressBar = ({ percent }) => (
  <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
    <div className="h-full bg-primary transition-all duration-500" style={{ width: `${Math.min(100, percent || 0)}%` }} />
  </div>
);

const RUNNING = ["Backup Running", "Reset Running", "Validation Running", "Reset Scheduled"];

// ---------------------------------------------------------------- Single school
function SingleSchoolDelete({ session, onDone }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState("");
  const [authorizedBy, setAuthorizedBy] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const search = async (val) => {
    setQ(val);
    if (val.trim().length < 2) { setResults([]); return; }
    try {
      const { tenants } = await dataMaintenanceApi.searchTenants(val.trim(), session);
      setResults(tenants || []);
    } catch { /* ignore */ }
  };

  const choose = async (t) => {
    setSelected(t); setResults([]); setQ(t.name); setPreview(null);
    try {
      const p = await dataMaintenanceApi.previewSchool(t.id, session);
      setPreview(p);
    } catch { toast.error("Failed to preview impact"); }
  };

  const canDelete = selected && reason.trim() && authorizedBy.trim() && confirm === "DELETE SCHOOL";

  const run = async () => {
    if (!canDelete) return;
    setBusy(true);
    try {
      const res = await dataMaintenanceApi.deleteSchool(
        { tenant_id: selected.id, confirm, reason: reason.trim(), authorized_by: authorizedBy.trim() }, session);
      toast.success(`Deleted "${selected.name}" — snapshot ${res.backup_id}`);
      setSelected(null); setPreview(null); setReason(""); setConfirm(""); setQ("");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-red-200 bg-white p-4" data-testid="danger-single-school">
      <div className="flex items-center gap-2 mb-1">
        <Building2 className="h-4 w-4 text-red-600" />
        <h4 className="text-sm font-semibold text-foreground">Delete a single school (cascading)</h4>
      </div>
      <p className="text-xs text-foreground/50 mb-3">Removes the tenant and all its users, licenses, diagnostics, answers and evidence. A 30-day recoverable snapshot is taken first.</p>

      <div className="relative">
        <div className="flex items-center gap-2 rounded-lg border border-border px-3">
          <Search className="h-3.5 w-3.5 text-foreground/40" />
          <input
            data-testid="ss-search"
            className="flex-1 bg-transparent py-2 text-sm outline-none"
            placeholder="Search schools by name or tenant id…"
            value={q}
            onChange={(e) => search(e.target.value)}
          />
        </div>
        {results.length > 0 && (
          <div className="absolute z-10 mt-1 w-full rounded-lg border border-border bg-white shadow-lg max-h-56 overflow-auto" data-testid="ss-results">
            {results.map((t) => (
              <button key={t.id} onClick={() => choose(t)} data-testid={`ss-result-${t.id}`}
                className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60 border-b border-border/40 last:border-0">
                <span className="font-medium text-foreground">{t.name}</span>
                <span className="text-foreground/40 ml-2 text-xs">{t.state || ""} · {t.id}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected && preview && (
        <div className="mt-3 rounded-lg border border-border bg-muted/20 p-3" data-testid="ss-preview">
          <div className="text-[13px] font-medium text-foreground mb-2">Impact — {preview.total.toLocaleString()} record(s) across {Object.values(preview.counts).filter((v) => v > 0).length} collection(s)</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 text-[12px]">
            {Object.entries(preview.counts).filter(([, v]) => v > 0).map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-border/30 py-0.5">
                <span className="text-foreground/60">{k}</span><span className="font-medium">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {selected && (
        <div className="mt-3 space-y-2">
          <Input data-testid="ss-reason" placeholder="Reason for deletion (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <Input data-testid="ss-authorized-by" placeholder="Authorized by (your name, required)" value={authorizedBy} onChange={(e) => setAuthorizedBy(e.target.value)} />
          <Input data-testid="ss-confirm" placeholder='Type "DELETE SCHOOL" to confirm' value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          <Button data-testid="ss-delete-btn" disabled={!canDelete || busy} onClick={run}
            className="w-full bg-red-600 hover:bg-red-700 text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
            Delete school permanently
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Bulk reset
function BulkReset({ session, onDone }) {
  const [authorizedBy, setAuthorizedBy] = useState("");
  const [confirm, setConfirm] = useState("");
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const poll = useRef(null);

  useEffect(() => () => clearInterval(poll.current), []);

  const canRun = authorizedBy.trim() && confirm === "RESET FOR PILOT";

  const startPolling = (jobId) => {
    clearInterval(poll.current);
    poll.current = setInterval(async () => {
      try {
        const j = await dataMaintenanceApi.bulkResetStatus(jobId, session);
        setJob(j);
        if (!RUNNING.includes(j.status)) { clearInterval(poll.current); onDone?.(); }
      } catch { /* ignore */ }
    }, 1500);
  };

  const run = async () => {
    if (!canRun) return;
    setBusy(true);
    try {
      const res = await dataMaintenanceApi.bulkReset({ confirm, authorized_by: authorizedBy.trim() }, session);
      toast.success("Reset started");
      setConfirm("");
      setJob({ id: res.job_id, status: res.status, percent: 0 });
      startPolling(res.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed to start");
    } finally { setBusy(false); }
  };

  const running = job && RUNNING.includes(job.status);

  return (
    <div className="rounded-xl border border-red-200 bg-white p-4" data-testid="danger-bulk-reset">
      <div className="flex items-center gap-2 mb-1">
        <RotateCcw className="h-4 w-4 text-red-600" />
        <h4 className="text-sm font-semibold text-foreground">Reset for Pilot — purge all test data</h4>
      </div>
      <p className="text-xs text-foreground/50 mb-3">Removes all test/junk records — test-pattern accounts (@example.com/.test), the named test users, test authorizers, test schools & portfolios, and ALL service inquiries. Protected internal accounts and real demo data (e.g. Ron's portfolio) are kept. Full snapshot taken first (30-day recovery).</p>

      {job && (
        <div className="mb-3 rounded-lg border border-border bg-muted/20 p-3" data-testid="bulk-job">
          <div className="flex items-center justify-between text-[13px] mb-2">
            <span className="font-medium text-foreground">{job.status}</span>
            <span className="text-foreground/50">{job.percent || 0}%</span>
          </div>
          <ProgressBar percent={job.percent} />
          {job.deleted && Object.keys(job.deleted).length > 0 && (
            <div className="mt-2 text-[11px] text-foreground/50">
              Deleted {Object.values(job.deleted).reduce((a, b) => a + b, 0).toLocaleString()} record(s)
            </div>
          )}
          {job.validation && (
            <div className={`mt-2 flex items-center gap-1.5 text-[12px] ${job.validation.ok ? "text-emerald-700" : "text-amber-700"}`}>
              {job.validation.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
              {job.validation.ok ? "Validation passed — all customer metrics at zero" : "Completed with warnings — review validation panel"}
            </div>
          )}
        </div>
      )}

      {!running && (
        <div className="space-y-2">
          <Input data-testid="bulk-authorized-by" placeholder="Authorized by (your name, required)" value={authorizedBy} onChange={(e) => setAuthorizedBy(e.target.value)} />
          <Input data-testid="bulk-confirm" placeholder='Type "RESET FOR PILOT" to confirm' value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          <Button data-testid="bulk-reset-btn" disabled={!canRun || busy} onClick={run}
            className="w-full bg-red-600 hover:bg-red-700 text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RotateCcw className="h-4 w-4 mr-2" />}
            Reset all customer data
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Directory rebuild
function DirectoryRebuild({ session, onDone }) {
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [authorizedBy, setAuthorizedBy] = useState("");
  const [confirm, setConfirm] = useState("");
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const poll = useRef(null);

  const loadStatus = () => dataMaintenanceApi.directoryStatus(session).then(setStatus).catch(() => {});
  useEffect(() => { loadStatus(); return () => clearInterval(poll.current); }, []); // eslint-disable-line

  const canRun = authorizedBy.trim() && confirm === "REBUILD SCHOOL DIRECTORY";

  const startPolling = (jobId) => {
    clearInterval(poll.current);
    poll.current = setInterval(async () => {
      try {
        const j = await dataMaintenanceApi.directoryRebuildStatus(jobId, session);
        setJob(j);
        if (!RUNNING.includes(j.status)) { clearInterval(poll.current); loadStatus(); onDone?.(); }
      } catch { /* ignore */ }
    }, 1500);
  };

  const run = async () => {
    if (!canRun) return;
    setBusy(true);
    try {
      const res = await dataMaintenanceApi.rebuildDirectory({ confirm, authorized_by: authorizedBy.trim() }, session);
      toast.success("Directory rebuild started");
      setConfirm("");
      setJob({ id: res.job_id, status: res.status, percent: 0 });
      startPolling(res.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rebuild failed to start");
    } finally { setBusy(false); }
  };

  const running = job && RUNNING.includes(job.status);
  const done = job && job.status === "Completed";

  return (
    <div className="rounded-xl border border-red-200 bg-white p-4" data-testid="danger-directory-rebuild">
      <div className="flex items-center gap-2 mb-1">
        <Database className="h-4 w-4 text-red-600" />
        <h4 className="text-sm font-semibold text-foreground">Rebuild the School Directory</h4>
      </div>
      <p className="text-xs text-foreground/50 mb-3">
        Wipes the entire national School Directory ({status ? status.count.toLocaleString() : "…"} records) so you can re-import a clean, deduplicated list. The directory is snapshotted (30-day recovery) and placed in maintenance mode while it clears.
      </p>

      {job && (
        <div className="mb-3 rounded-lg border border-border bg-muted/20 p-3" data-testid="dir-job">
          <div className="flex items-center justify-between text-[13px] mb-2">
            <span className="font-medium text-foreground">{job.status}</span>
            <span className="text-foreground/50">{job.percent || 0}%</span>
          </div>
          <ProgressBar percent={job.percent} />
          <div className="mt-2 text-[11px] text-foreground/50">
            {job.backed_up ? `Backed up ${job.backed_up.toLocaleString()} · ` : ""}
            {job.deleted_count ? `Deleted ${job.deleted_count.toLocaleString()}` : ""}
          </div>
          {done && (
            <div className="mt-2">
              <div className="flex items-center gap-1.5 text-[12px] text-emerald-700 mb-2">
                <CheckCircle2 className="h-3.5 w-3.5" /> Directory cleared. Re-import your deduplicated list next.
              </div>
              <Button data-testid="dir-goto-import" size="sm" variant="outline" onClick={() => nav("/app/schools")}>
                <Upload className="h-3.5 w-3.5 mr-1.5" /> Go to Import
              </Button>
            </div>
          )}
        </div>
      )}

      {!running && !done && (
        <div className="space-y-2">
          <Input data-testid="dir-authorized-by" placeholder="Authorized by (your name, required)" value={authorizedBy} onChange={(e) => setAuthorizedBy(e.target.value)} />
          <Input data-testid="dir-confirm" placeholder='Type "REBUILD SCHOOL DIRECTORY" to confirm' value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          <Button data-testid="dir-rebuild-btn" disabled={!canRun || busy} onClick={run}
            className="w-full bg-red-600 hover:bg-red-700 text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Database className="h-4 w-4 mr-2" />}
            Wipe & rebuild directory
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Validation + history
function ValidationPanel({ session }) {
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = () => { setLoading(true); dataMaintenanceApi.validate(session).then(setV).catch(() => toast.error("Validation failed")).finally(() => setLoading(false)); };
  return (
    <div className="rounded-xl border border-border p-4" data-testid="validation-panel">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-foreground">Post-reset validation</h4>
        <Button size="sm" variant="outline" onClick={run} data-testid="validate-btn">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />} Run check
        </Button>
      </div>
      {v ? (
        <>
          <div className={`flex items-center gap-1.5 text-[13px] mb-2 ${v.ok ? "text-emerald-700" : "text-amber-700"}`}>
            {v.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
            {v.ok ? "All customer metrics at zero." : "Some customer records remain."}
          </div>
          <div className="grid grid-cols-2 gap-1 text-[12px]">
            {Object.entries(v.checks).map(([k, val]) => (
              <div key={k} className="flex justify-between border-b border-border/30 py-0.5">
                <span className="text-foreground/60">{k.replace(/_/g, " ")}</span>
                <span className={`font-medium ${k === "protected_present" ? "text-emerald-700" : val ? "text-amber-700" : "text-foreground"}`}>{typeof val === "number" ? val.toLocaleString() : String(val)}</span>
              </div>
            ))}
          </div>
        </>
      ) : <p className="text-xs text-foreground/40">Run a check to confirm revenue, pipeline and customer records are zeroed (protected accounts stay).</p>}
    </div>
  );
}

function HistoryPanel({ session, refreshKey }) {
  const [jobs, setJobs] = useState([]);
  const [backups, setBackups] = useState([]);
  useEffect(() => {
    dataMaintenanceApi.listJobs(session).then((d) => setJobs(d.jobs || [])).catch(() => {});
    dataMaintenanceApi.listBackups(session).then((d) => setBackups(d.backups || [])).catch(() => {});
  }, [session, refreshKey]);
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="rounded-xl border border-border p-4" data-testid="jobs-panel">
        <h4 className="text-sm font-semibold text-foreground mb-2">Recent jobs</h4>
        {jobs.length ? jobs.map((j) => (
          <div key={j.id} className="flex items-center justify-between text-[12px] border-b border-border/30 py-1.5">
            <span className="text-foreground/60">{j.kind}</span>
            <span className={`font-medium ${j.status === "Completed" ? "text-emerald-700" : j.status === "Failed" ? "text-red-600" : "text-foreground"}`}>{j.status}</span>
          </div>
        )) : <p className="text-xs text-foreground/40">No jobs yet.</p>}
      </div>
      <div className="rounded-xl border border-border p-4" data-testid="backups-panel">
        <div className="flex items-center gap-1.5 mb-2"><Archive className="h-3.5 w-3.5 text-foreground/50" /><h4 className="text-sm font-semibold text-foreground">Recent snapshots</h4></div>
        {backups.length ? backups.slice(0, 8).map((b) => (
          <div key={b.id} className="flex items-center justify-between text-[12px] border-b border-border/30 py-1.5">
            <span className="text-foreground/60 truncate mr-2">{b.label}</span>
            <span className="font-medium text-foreground shrink-0">{(b.record_count || 0).toLocaleString()} recs</span>
          </div>
        )) : <p className="text-xs text-foreground/40">No snapshots yet.</p>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Page
export default function DataMaintenance({ session }) {
  const [inv, setInv] = useState(null);
  const [prot, setProt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = () => {
    setLoading(true);
    Promise.all([dataMaintenanceApi.getInventory(session), dataMaintenanceApi.getProtected(session)])
      .then(([i, p]) => { setInv(i); setProt(p); })
      .catch(() => toast.error("Failed to load data inventory"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [session]); // eslint-disable-line
  const refreshAll = () => { load(); setRefreshKey((k) => k + 1); };

  if (loading) return <div className="flex items-center gap-2 text-sm text-foreground/50 py-10" data-testid="datamaint-loading"><Loader2 className="h-4 w-4 animate-spin" /> Inventorying data…</div>;
  if (!inv || !prot) return null;

  return (
    <div className="space-y-6" data-testid="data-maintenance">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Database className="h-4 w-4 text-primary" />
          <div>
            <h2 className="text-sm font-semibold text-foreground">Data Maintenance — Pilot Reset</h2>
            <p className="text-xs text-foreground/40">Inventory, protected-account allowlist, and destructive reset tooling.</p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={refreshAll} data-testid="datamaint-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh</Button>
      </div>

      {/* Protected accounts */}
      <div className="rounded-xl border border-border p-4" data-testid="datamaint-protected">
        <div className="flex items-center gap-2 mb-3">
          <Lock className="h-4 w-4 text-emerald-600" />
          <h3 className="text-sm font-semibold text-foreground">Protected accounts (retained)</h3>
        </div>
        <div className="space-y-1.5">
          {prot.allowlist_emails.map((e) => {
            const found = prot.protected.filter((p) => (p.email || "").toLowerCase() === e);
            return (
              <div key={e} className="flex items-center justify-between text-[13px] border-b border-border/50 py-1.5" data-testid={`datamaint-protected-${e}`}>
                <span className="font-medium text-foreground">{e}</span>
                {found.length ? (
                  <span className="text-emerald-700 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Resolved in {found.map((f) => f.collection).join(", ")}</span>
                ) : (
                  <span className="text-amber-700">Not found in this database</span>
                )}
              </div>
            );
          })}
        </div>
        {prot.collisions.length > 0 && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="datamaint-collisions">
            <div className="flex items-center gap-2 text-[13px] font-semibold text-amber-900"><AlertTriangle className="h-4 w-4" /> {prot.collisions.length} name-collision record(s) — NOT protected, will be removed</div>
            <ul className="mt-2 space-y-1 text-[12px] text-amber-900/80">
              {prot.collisions.map((c, i) => (
                <li key={i}>{c.name} — <span className="font-mono">{c.email}</span> ({c.collection}, {c.role || "—"})</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Customer/txn records" value={inv.grand_total.toLocaleString()} />
        <Stat label="School directory" value={inv.school_directory_count.toLocaleString()} />
        <Stat label="Protected resolved" value={inv.protected_summary.resolved_count} />
        <Stat label="Name collisions" value={inv.protected_summary.collision_count} tone={inv.protected_summary.collision_count ? "warn" : "default"} />
      </div>

      {/* Inventory groups */}
      {inv.groups.map((g) => (
        <div key={g.group} data-testid={`datamaint-group-${g.group.replace(/\s+/g, "-").toLowerCase()}`}>
          <p className="text-[11px] uppercase tracking-wider font-semibold text-foreground/50 mb-2">{g.group}</p>
          <div className="rounded-xl border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-foreground/60 text-xs">
                <tr>
                  <th className="text-left px-3 py-2">Entity</th>
                  <th className="text-right px-3 py-2">Total</th>
                  <th className="text-right px-3 py-2">Pilot</th>
                  <th className="text-right px-3 py-2">Test</th>
                  <th className="text-right px-3 py-2">Protected</th>
                </tr>
              </thead>
              <tbody>
                {g.items.map((it) => (
                  <tr key={it.collection} className="border-t border-border">
                    <td className="px-3 py-2 text-foreground">{it.label}</td>
                    <td className="px-3 py-2 text-right font-medium text-foreground">{it.total.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right text-blue-600">{it.pilot || "—"}</td>
                    <td className="px-3 py-2 text-right text-foreground/50">{it.test || "—"}</td>
                    <td className="px-3 py-2 text-right text-emerald-600">{it.protected || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* DANGER ZONE */}
      <div className="rounded-2xl border-2 border-red-300 bg-red-50/40 p-4 sm:p-5 space-y-4" data-testid="danger-zone">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          <div>
            <h3 className="text-sm font-bold text-red-700 uppercase tracking-wide">Danger zone — destructive actions</h3>
            <p className="text-xs text-red-700/70">Every action snapshots to recoverable storage first (30-day retention). Type the exact confirmation phrase to enable each button.</p>
          </div>
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          <SingleSchoolDelete session={session} onDone={refreshAll} />
          <BulkReset session={session} onDone={refreshAll} />
        </div>
        <DirectoryRebuild session={session} onDone={refreshAll} />
      </div>

      <ValidationPanel session={session} />
      <HistoryPanel session={session} refreshKey={refreshKey} />
    </div>
  );
}
